import type { EchoflicksSessionMedia } from "../types";
import type { FramingSceneView } from "./framingDiagnostics";

type MediaCorrelation = {
  sceneId: string | null;
  attemptId: string | null;
  taskId: string | null;
  planDigest: string | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function mediaCorrelation(media: EchoflicksSessionMedia): MediaCorrelation {
  const raw = isRecord(media.raw_metadata) ? media.raw_metadata : {};
  return {
    sceneId:
      media.scene_id == null
        ? stringValue(raw.scene_id)
        : String(media.scene_id),
    attemptId: stringValue(raw.attempt_id ?? raw.attempt),
    taskId: media.task_id ?? stringValue(raw.task_id),
    planDigest: stringValue(raw.plan_digest),
  };
}

function correlationScore(
  media: EchoflicksSessionMedia,
  scene: FramingSceneView,
): number {
  const value = mediaCorrelation(media);
  let score = 0;
  if (value.sceneId && value.sceneId !== scene.sceneId) return -1;
  if (value.sceneId === scene.sceneId) score += 8;
  if (value.attemptId && scene.attemptId) {
    if (value.attemptId !== scene.attemptId) return -1;
    score += 4;
  }
  if (value.taskId && scene.taskId) {
    if (value.taskId !== scene.taskId) return -1;
    score += 2;
  }
  if (value.planDigest && scene.planDigest) {
    if (value.planDigest !== scene.planDigest) return -1;
    score += 4;
  }
  return score;
}

function compareMedia(
  left: EchoflicksSessionMedia,
  right: EchoflicksSessionMedia,
): number {
  return (
    String(left.created_at_source ?? "").localeCompare(
      String(right.created_at_source ?? ""),
    ) || String(left.id).localeCompare(String(right.id))
  );
}

export function selectLatestFramingReport(
  media: EchoflicksSessionMedia[],
): EchoflicksSessionMedia | undefined {
  return media
    .filter((item) => item.kind === "framing_report")
    .sort(compareMedia)
    .at(-1);
}

export function selectCorrelatedFramingDiagnostic(
  media: EchoflicksSessionMedia[],
  scene: FramingSceneView,
): EchoflicksSessionMedia | undefined {
  return (
    media
      .filter((item) => item.kind === "framing_diagnostic")
      .map((item) => ({ item, score: correlationScore(item, scene) }))
      // A diagnostic without any shared scene/attempt/task/digest identity is not
      // evidence for this scene. Fail closed instead of reusing the newest image.
      .filter(({ score }) => score > 0)
      .sort(
        (left, right) =>
          left.score - right.score || compareMedia(left.item, right.item),
      )
      .at(-1)?.item
  );
}
